// src/screens/DriverApproval.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import useIdleLogout from "../hooks/useIdleLogout";
import "../css/Dashboard.css";
import "../css/DriverApproval.css";

const API_DRIVERS   = (process.env.REACT_APP_DRIVERS_API_URL  || "").trim();
const BASEURL_ADMIN = (process.env.REACT_APP_BASEURL_ADMIN    || "").trim();
const PAGE_SIZE     = 10;

const DEFAULT_AVATAR = process.env.PUBLIC_URL + "/assets/img/admin profile.jpg";

/* ---- helpers ---- */
function resolveImg(src) {
  if (!src) return DEFAULT_AVATAR;
  if (/^https?:\/\//i.test(src) || src.startsWith("data:") || src.startsWith("blob:")) return src;
  if (!BASEURL_ADMIN) return DEFAULT_AVATAR;
  return `${BASEURL_ADMIN.replace(/\/+$/, "")}/${src.replace(/^\/+/, "")}`;
}

function relTime(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function isExpired(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function getDriverId(d) {
  return d.driver_id ?? d.id ?? d.user_id ?? null;
}

function getStatus(d) {
  return (d.approval_status || "pending").toLowerCase();
}

/* ---- Status badge (inline) ---- */
function StatusBadge({ status }) {
  return (
    <span className={`status-badge ${status}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/* ================== Main Page ================== */
export default function DriverApproval() {
  useIdleLogout();

  const [tab, setTab]         = useState("pending");
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing]   = useState(false);
  const [error, setError]     = useState("");
  const [page, setPage]       = useState(1);

  const token = localStorage.getItem("access_token") || "";
  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const load = useCallback(async () => {
    if (!API_DRIVERS) {
      setError("Missing REACT_APP_DRIVERS_API_URL in .env");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_DRIVERS, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const arr = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      setDrivers(arr);
    } catch (e) {
      setError(e.message || "Failed to load drivers");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 whenever the tab changes
  useEffect(() => { setPage(1); }, [tab]);

  const counts = useMemo(() => ({
    all:      drivers.length,
    pending:  drivers.filter(d => getStatus(d) === "pending").length,
    approved: drivers.filter(d => getStatus(d) === "approved").length,
    rejected: drivers.filter(d => getStatus(d) === "rejected").length,
  }), [drivers]);

  const filtered = useMemo(() => {
    if (tab === "all") return drivers;
    return drivers.filter(d => getStatus(d) === tab);
  }, [drivers, tab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  /* ---- View (Swal modal) ---- */
  const handleView = (driver) => {
    const status  = getStatus(driver);
    const vehicle = driver.vehicles?.[0] || {};
    const vehText = [vehicle.make, vehicle.model, vehicle.color, vehicle.license_plate, vehicle.vehicle_type]
      .filter(Boolean).join(" • ") || "—";
    const expDate  = driver.license_expiry;
    const expired  = isExpired(expDate);
    const driverName    = driver.user_name || driver.name || "—";
    const avatarSrc     = resolveImg(driver.profile_image);
    const submittedText = relTime(driver.registered_at || driver.created_at);

    // Pre-compute each fragment to avoid deeply nested template literal issues
    const badgeStyle =
      status === "approved"
        ? "background:rgba(34,197,94,.15);color:#15803d;border:1px solid rgba(34,197,94,.3);"
        : status === "rejected"
        ? "background:rgba(239,68,68,.13);color:#b91c1c;border:1px solid rgba(239,68,68,.25);"
        : "background:rgba(245,158,11,.15);color:#b45309;border:1px solid rgba(245,158,11,.3);";

    const emailHtml = driver.email
      ? `<i class="bi bi-envelope" style="margin-right:3px;"></i>${driver.email}`
      : "";
    const phoneHtml = driver.phone
      ? `<span style="margin-left:8px;"><i class="bi bi-phone" style="margin-right:3px;"></i>${driver.phone}</span>`
      : "";

    const expiryColor = expired ? "color:#dc3545;font-weight:700;" : "color:#6b7280;";
    const expiryLabel = expired ? `Exp: ${fmtDate(expDate)} ⚠ EXPIRED` : `Exp: ${fmtDate(expDate)}`;
    const expiryHtml  = expDate
      ? `<span style="margin-left:auto;font-size:.76rem;${expiryColor}">${expiryLabel}</span>`
      : "";

    const docsHtml = driver.documents?.length > 0
      ? "<div style=\"display:flex;flex-wrap:wrap;gap:6px;margin-top:12px;\">" +
        driver.documents.map((doc, i) => {
          const label = (doc.document_type || ("Doc " + (i + 1))).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
          const href  = resolveImg(doc.document_url);
          return `<a href="${href}" target="_blank" rel="noopener noreferrer"
            style="display:inline-flex;align-items:center;gap:5px;padding:.28rem .65rem;border-radius:8px;
              border:1.5px solid rgba(13,110,253,.3);background:rgba(13,110,253,.06);color:#0b5ed7;
              font-size:.76rem;font-weight:600;text-decoration:none;">
            <i class="bi bi-file-earmark-text"></i> ${label}
          </a>`;
        }).join("") +
        "</div>"
      : "";

    const rejectionHtml = status === "rejected" && driver.rejection_reason
      ? `<div style="background:rgba(239,68,68,.07);border:1px solid rgba(239,68,68,.2);border-radius:8px;
             padding:.5rem .75rem;font-size:.82rem;color:#b91c1c;margin-top:12px;">
           <strong style="display:block;margin-bottom:.25rem;">Rejection reason:</strong>
           ${driver.rejection_reason}
         </div>`
      : "";

    Swal.fire({
      width: 680,
      padding: "2rem",
      showConfirmButton: false,
      showCloseButton: true,
      html: `
        <div style="text-align:left;padding-top:8px;padding-right:44px;">

          <!-- avatar + name + badge -->
          <div style="display:flex;align-items:center;gap:18px;margin-bottom:24px;">
            <img src="${avatarSrc}"
              onerror="this.onerror=null;this.src='${DEFAULT_AVATAR}'"
              style="width:72px;height:72px;border-radius:50%;object-fit:cover;flex-shrink:0;border:3px solid rgba(13,110,253,.2);" />
            <div style="flex:1;min-width:0;">
              <div style="font-weight:700;font-size:1.15rem;color:#111827;margin-bottom:6px;">${driverName}</div>
              <div style="font-size:.88rem;color:#6b7280;display:flex;flex-wrap:wrap;gap:12px;">
                ${emailHtml}${phoneHtml}
              </div>
            </div>
            <span style="flex-shrink:0;padding:.35rem .9rem;border-radius:999px;font-size:.8rem;font-weight:700;${badgeStyle}">
              ${status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>

          <hr style="margin:0 0 20px;opacity:.1;" />

          <!-- license -->
          <div style="display:flex;align-items:center;gap:12px;font-size:.92rem;margin-bottom:16px;color:#374151;">
            <i class="bi bi-card-text" style="color:#9ca3af;font-size:1rem;flex-shrink:0;"></i>
            <span style="color:#6b7280;flex-shrink:0;min-width:70px;">License</span>
            <span style="font-weight:600;">${driver.license_number || "—"}</span>
            ${expiryHtml}
          </div>

          <!-- vehicle -->
          <div style="display:flex;align-items:center;gap:12px;font-size:.92rem;margin-bottom:16px;color:#374151;">
            <i class="bi bi-truck" style="color:#9ca3af;font-size:1rem;flex-shrink:0;"></i>
            <span style="color:#6b7280;flex-shrink:0;min-width:70px;">Vehicle</span>
            <span style="font-weight:500;">${vehText}</span>
          </div>

          <!-- submitted -->
          <div style="display:flex;align-items:center;gap:12px;font-size:.85rem;color:#9ca3af;margin-bottom:8px;">
            <i class="bi bi-clock" style="font-size:.95rem;flex-shrink:0;"></i>
            <span>Submitted ${submittedText}</span>
          </div>

          ${docsHtml}
          ${rejectionHtml}
        </div>
      `,
    });
  };

  /* ---- Approve ---- */
  const handleApprove = async (driver) => {
    const driverId = getDriverId(driver);
    const name = driver.user_name || driver.name || `Driver #${driverId}`;

    const confirm = await Swal.fire({
      icon: "question",
      title: "Approve driver?",
      text: `Approve "${name}"? They will be able to log in immediately.`,
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      confirmButtonColor: "#00B14F",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setActing(true);
    try {
      const url = `${API_DRIVERS.replace(/\/+$/, "")}/${driverId}/approve`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ action: "approved" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await Swal.fire({ icon: "success", title: "Driver Approved!", text: `${name} can now log in.`, timer: 2000, showConfirmButton: false });
      load();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setActing(false);
    }
  };

  /* ---- Reject ---- */
  const handleReject = async (driver) => {
    const driverId = getDriverId(driver);
    const name = driver.user_name || driver.name || `Driver #${driverId}`;

    const quickReasons = ["Expired license", "Blurry documents", "Vehicle mismatch", "Incomplete info"];

    const result = await Swal.fire({
      title: `Reject "${name}"`,
      html: `
        <p style="text-align:left;font-size:.85rem;color:#6b7280;margin:0 0 10px;">
          This message will be shown to the driver in the app.
        </p>
        <textarea
          id="swal-reason"
          class="swal2-textarea"
          placeholder="e.g. License photo is blurry. Please re-register with a clear image."
          style="min-height:110px;resize:vertical;text-align:left;font-size:.9rem;"
        ></textarea>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px;justify-content:flex-start;">
          <span style="font-size:.78rem;color:#6b7280;align-self:center;">Quick:</span>
          ${quickReasons.map(r =>
            `<button type="button"
              onclick="var t=document.getElementById('swal-reason');t.value+=(t.value&&!t.value.endsWith(' ')?'. ':'')+('${r}.');"
              style="padding:3px 10px;border-radius:999px;border:1px solid #d0d5dd;background:#f9fafb;cursor:pointer;font-size:.76rem;font-weight:600;"
            >${r}</button>`
          ).join("")}
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Reject Driver",
      confirmButtonColor: "#ef4444",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = document.getElementById("swal-reason")?.value?.trim();
        if (!reason) {
          Swal.showValidationMessage("Rejection reason is required");
          return null;
        }
        return reason;
      },
    });

    if (!result.value) return;

    setActing(true);
    try {
      const url = `${API_DRIVERS.replace(/\/+$/, "")}/${driverId}/approve`;
      const res = await fetch(url, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ action: "rejected", rejection_reason: result.value }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await Swal.fire({ icon: "success", title: "Driver Rejected", timer: 1800, showConfirmButton: false });
      load();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setActing(false);
    }
  };

  /* ---- Pagination helpers ---- */
  const pageRange = useMemo(() => {
    const delta = 2;
    const range = [];
    const left  = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    if (left > 1) { range.push(1); if (left > 2) range.push("…"); }
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages) { if (right < totalPages - 1) range.push("…"); range.push(totalPages); }
    return range;
  }, [page, totalPages]);

  const TABS = [
    { key: "pending",  label: "Pending",  cls: "tab-pending"  },
    { key: "approved", label: "Approved", cls: "tab-approved" },
    { key: "rejected", label: "Rejected", cls: "tab-rejected" },
    { key: "all",      label: "All",      cls: ""             },
  ];

  const startRow = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRow   = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="container-fluid px-4 approval-page" style={{ minHeight: "calc(100vh - 90px)", display: "flex", flexDirection: "column" }}>

      {/* Header */}
      <div className="approval-header">
        <h2><i className="bi bi-person-check-fill me-2" style={{ color: "var(--primary, #0d6efd)" }} />Driver Approvals</h2>
      </div>

      {/* Tabs */}
      <div className="approval-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`approval-tab ${t.cls} ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className="tab-badge">{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {/* States */}
      {error && (
        <div className="approval-state">
          <i className="bi bi-exclamation-triangle" />
          {error}
        </div>
      )}

      {!error && loading && (
        <div className="approval-state">
          <i className="bi bi-hourglass-split" />
          Loading drivers…
        </div>
      )}

      {!error && !loading && filtered.length === 0 && (
        <div className="approval-state">
          <i className="bi bi-inbox" />
          {tab === "pending" ? "No pending drivers — all clear!" : `No ${tab} drivers.`}
        </div>
      )}

      {/* Table */}
      {!error && !loading && filtered.length > 0 && (
        <div className="drivers-table-wrap">
          <table className="drivers-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Driver</th>
                <th>Contact</th>
                <th>License</th>
                <th>Vehicle</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((d, i) => {
                const status  = getStatus(d);
                const vehicle = d.vehicles?.[0] || {};
                const vehText = [vehicle.make, vehicle.model, vehicle.color, vehicle.license_plate]
                  .filter(Boolean).join(" • ") || "—";
                const expDate = d.license_expiry;
                const expired = isExpired(expDate);
                const rowNum  = startRow + i;

                return (
                  <tr key={getDriverId(d) ?? i}>
                    <td style={{ color: "var(--secondary, #9ca3af)", fontWeight: 500 }}>{rowNum}</td>

                    {/* Driver */}
                    <td>
                      <div className="tbl-driver-cell">
                        <img
                          className="tbl-avatar"
                          src={resolveImg(d.profile_image)}
                          alt={d.user_name || "driver"}
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_AVATAR; }}
                        />
                        <span className="tbl-driver-name">{d.user_name || d.name || "—"}</span>
                      </div>
                    </td>

                    {/* Contact */}
                    <td>
                      <div style={{ fontSize: ".8rem", color: "var(--secondary, #6b7280)", lineHeight: 1.6 }}>
                        {d.email && <div><i className="bi bi-envelope" style={{ marginRight: 4 }} />{d.email}</div>}
                        {d.phone && <div><i className="bi bi-phone"    style={{ marginRight: 4 }} />{d.phone}</div>}
                        {!d.email && !d.phone && "—"}
                      </div>
                    </td>

                    {/* License */}
                    <td>
                      <div style={{ fontSize: ".83rem" }}>
                        <div style={{ fontWeight: 600 }}>{d.license_number || "—"}</div>
                        {expDate && (
                          <div style={{ fontSize: ".75rem", marginTop: 2, color: expired ? "#dc3545" : "var(--secondary, #9ca3af)", fontWeight: expired ? 700 : 400 }}>
                            Exp: {fmtDate(expDate)} {expired && "⚠"}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Vehicle */}
                    <td style={{ fontSize: ".82rem", maxWidth: 160, wordBreak: "break-word" }}>{vehText}</td>

                    {/* Status */}
                    <td><StatusBadge status={status} /></td>

                    {/* Submitted */}
                    <td style={{ fontSize: ".8rem", color: "var(--secondary, #9ca3af)", whiteSpace: "nowrap" }}>
                      {relTime(d.registered_at || d.created_at)}
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="tbl-actions">
                        <button className="btn-view" onClick={() => handleView(d)} title="View details">
                          <i className="bi bi-eye" />
                        </button>
                        {status === "pending" && (
                          <>
                            <button className="btn-approve-sm" onClick={() => handleApprove(d)} disabled={acting}>
                              <i className="bi bi-check-circle-fill" /> Approve
                            </button>
                            <button className="btn-reject-sm" onClick={() => handleReject(d)} disabled={acting}>
                              <i className="bi bi-x-circle-fill" /> Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination bar */}
          <div className="pagination-bar">
            <span>Showing {startRow}–{endRow} of {filtered.length} driver{filtered.length !== 1 ? "s" : ""}</span>
            <div className="pagination-controls">
              <button className="pg-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                <i className="bi bi-chevron-left" />
              </button>
              {pageRange.map((p, i) =>
                p === "…"
                  ? <span key={`ellipsis-${i}`} style={{ padding: "0 .25rem", color: "var(--secondary, #9ca3af)" }}>…</span>
                  : <button key={p} className={`pg-btn ${page === p ? "active" : ""}`} onClick={() => setPage(p)}>{p}</button>
              )}
              <button className="pg-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                <i className="bi bi-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
